import React, { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { X, Search, Plus } from "lucide-react";

/**
 * Searchable multi-select of code entries.
 * type: "cpt" | "icd10"
 *
 * Users can pick from the searchable catalog OR add a custom code that
 * isn't in the database (e.g. a brand-new CPT their team uses).
 */
export default function CodeMultiSelect({ label, type, value = [], onChange, max = 10, testId }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customCode, setCustomCode] = useState("");
  const [customDesc, setCustomDesc] = useState("");
  const wrapRef = useRef(null);

  const codeLabel = type === "cpt" ? "CPT" : "ICD-10";

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setShowCustom(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    let alive = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/codes/${type}`, { params: { q } });
        if (alive) setResults(data || []);
      } finally {
        if (alive) setLoading(false);
      }
    }, 200);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q, type]);

  const add = (item) => {
    if (value.length >= max) return;
    if (value.find((v) => v.code === item.code)) return;
    onChange([...value, item]);
    setQ("");
  };
  const remove = (code) => onChange(value.filter((v) => v.code !== code));

  const openCustom = () => {
    // Prefill custom code with the current query if it looks code-like
    const looksLikeCode = /^[A-Z0-9][A-Z0-9.]*$/i.test(q.trim());
    setCustomCode(looksLikeCode ? q.trim().toUpperCase() : "");
    setCustomDesc(looksLikeCode ? "" : q.trim());
    setShowCustom(true);
    setOpen(false);
  };

  const addCustom = () => {
    const code = customCode.trim();
    const desc = customDesc.trim();
    if (!code) return;
    if (value.find((v) => v.code.toLowerCase() === code.toLowerCase())) {
      setShowCustom(false);
      return;
    }
    if (value.length >= max) return;
    onChange([...value, { code, description: desc || "Custom code (no description)" }]);
    setCustomCode("");
    setCustomDesc("");
    setShowCustom(false);
    setQ("");
  };

  return (
    <div ref={wrapRef} className="relative">
      <label className="block text-sm font-medium mb-2">
        {label} <span className="subtle text-xs">({value.length}/{max})</span>
      </label>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2" data-testid={`${testId}-selected`}>
          {value.map((v) => (
            <span key={v.code} className="chip chip-green">
              <span className="font-semibold">{v.code}</span>
              <button
                type="button"
                onClick={() => remove(v.code)}
                data-testid={`${testId}-remove-${v.code}`}
                className="ml-1 hover:text-red-600"
                aria-label={`Remove ${v.code}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neu-muted pointer-events-none" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={`Search ${codeLabel} codes (min 2 chars)`}
          className="neu-input pl-10"
          data-testid={testId}
          disabled={value.length >= max}
        />
      </div>

      {open && q.trim().length >= 2 && !showCustom && (
        <div
          className="absolute z-30 mt-2 left-0 right-0 max-h-64 overflow-y-auto rounded-xl bg-white shadow-neu border border-neu-muted/20"
          data-testid={`${testId}-results`}
        >
          {loading && <div className="p-3 text-sm subtle">Searching…</div>}
          {!loading && results.length === 0 && (
            <div className="p-3 text-sm subtle flex items-center justify-between gap-2">
              <span>No matches for “{q}”.</span>
              <button
                type="button"
                onClick={openCustom}
                className="chip chip-green inline-flex items-center gap-1 hover:opacity-90"
                data-testid={`${testId}-add-custom`}
              >
                <Plus className="w-3 h-3" /> Add custom {codeLabel}
              </button>
            </div>
          )}
          {!loading &&
            results.map((r) => {
              const selected = value.find((v) => v.code === r.code);
              return (
                <button
                  type="button"
                  key={r.code}
                  onClick={() => !selected && add(r)}
                  disabled={!!selected || value.length >= max}
                  data-testid={`${testId}-add-${r.code}`}
                  className={`w-full text-left px-3 py-2 hover:bg-neu-bg transition-colors border-b border-neu-muted/10 last:border-b-0 ${
                    selected ? "opacity-40" : ""
                  }`}
                >
                  <div className="font-semibold text-neu-text">{r.code}</div>
                  <div className="text-xs subtle line-clamp-1">{r.description}</div>
                </button>
              );
            })}
          {!loading && results.length > 0 && (
            <button
              type="button"
              onClick={openCustom}
              className="w-full text-left px-3 py-2 hover:bg-neu-bg border-t border-neu-muted/20 flex items-center gap-2 text-sm text-neu-accent"
              data-testid={`${testId}-add-custom`}
            >
              <Plus className="w-4 h-4" /> Not listed? Add a custom {codeLabel} code
            </button>
          )}
        </div>
      )}

      {showCustom && (
        <div
          className="absolute z-30 mt-2 left-0 right-0 rounded-xl bg-white shadow-neu border border-neu-muted/20 p-4 space-y-3"
          data-testid={`${testId}-custom-form`}
        >
          <div className="text-sm font-medium text-neu-text">
            Add a custom {codeLabel} code
          </div>
          <input
            type="text"
            value={customCode}
            onChange={(e) => setCustomCode(e.target.value)}
            placeholder={type === "cpt" ? "e.g. 21XXX" : "e.g. S01.311A"}
            className="neu-input"
            data-testid={`${testId}-custom-code`}
            autoFocus
          />
          <input
            type="text"
            value={customDesc}
            onChange={(e) => setCustomDesc(e.target.value)}
            placeholder="Description (optional)"
            className="neu-input"
            data-testid={`${testId}-custom-desc`}
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowCustom(false)}
              className="btn-neu text-sm"
              data-testid={`${testId}-custom-cancel`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={addCustom}
              disabled={!customCode.trim()}
              className="btn-accent text-sm inline-flex items-center gap-1"
              data-testid={`${testId}-custom-save`}
            >
              <Plus className="w-3 h-3" /> Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
